import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { combineLatest, Observable, Subject } from 'rxjs';
import { IntegrationVersionEnum, LiabilityDataModel } from '../../models/liability-data.model';
import { PaymentTypeEnum } from '../../models/payment-type.model';
import { BrandService } from '../../services/brand.service';
import { BrandId } from '../../models/brand.model';
import { environment } from '../../../environments/environment';
import { PaymentSummaryService } from '../../services/payment-summary.service';
import { PaymentSummary } from '../../models/payment-summary.model';
import { PersonalData } from 'src/app/services/payment.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent {
  cdnAddress = environment.cdnAddress;
  logoLoadingFailed: boolean;
  data$: Observable<HeaderData>;
  private type$ = new Subject<PaymentTypeEnum>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private brandService: BrandService,
    private paymentSummaryService: PaymentSummaryService
  ) {
    this.data$ = combineLatest([this.getPayment(), this.getData()]).pipe(
      map(([payment, { paymentType, typeToggleVisible, onlyBalance, headerPaymentSummaryVisible, personalData }]) => ({
        payment,
        paymentType,
        onlyBalance,
        typeToggleVisible:
          typeToggleVisible &&
          !onlyBalance &&
          !!payment &&
          payment.integrationVersion === IntegrationVersionEnum.V1 &&
          payment.balance?.amountToPay > payment.amountToPay &&
          payment.balance?.enabled !== false,
        brand: this.brandService.brand.id,
        paymentSummary: this.getPaymentSummary(paymentType, headerPaymentSummaryVisible),
        personalData
      })),
      tap(data => {
        if (data.paymentType === PaymentTypeEnum.LIABILITY && data.onlyBalance) {
          return this.changeType(PaymentTypeEnum.BALANCE);
        }
      })
    );
  }

  private getPaymentSummary(
    paymentType: PaymentTypeEnum,
    headerPaymentSummaryVisible: boolean
  ): PaymentSummary | undefined {
    if (headerPaymentSummaryVisible) {
      return this.paymentSummaryService.calculateSummary(paymentType);
    } else {
      return undefined;
    }
  }

  changeType(type: PaymentTypeEnum) {
    this.type$.next(type);
    return this.router.navigate([], {
      queryParams: { type: type === PaymentTypeEnum.BALANCE ? 'balance' : '' },
      queryParamsHandling: 'merge'
    });
  }

  private getPayment() {
    return this.getChildRouteObservable().pipe(
      switchMap(route => route.data),
      map(data => data.payment)
    );
  }

  private getData() {
    return this.getChildRouteObservable().pipe(switchMap(route => route.data));
  }

  private getChildRouteObservable() {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let child = this.route;
        while (child.firstChild) {
          child = child.firstChild;
        }
        return child;
      })
    );
  }
}

interface HeaderData {
  typeToggleVisible: boolean;
  payment: LiabilityDataModel;
  paymentType: PaymentTypeEnum;
  onlyBalance: boolean;
  brand: BrandId;
  paymentSummary?: PaymentSummary;
  personalData?: PersonalData;
}
